/**
 * LanguageSelector - Dropdown for selecting both UI and transcription language
 */

import { SUPPORTED_LANGUAGES } from '@refinevoice/shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../store/useLanguageStore';
import './LanguageSelector.css';

export function LanguageSelector() {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { selectedTranscriptionLanguage, setTranscriptionLanguage, setUILanguage } =
    useLanguageStore();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const handleLanguageSelect = (language: (typeof SUPPORTED_LANGUAGES)[0]) => {
    // Set both transcription and UI language to the same
    setTranscriptionLanguage(language);
    setUILanguage(language.code);
    setShowDropdown(false);
  };

  return (
    <div className="language-selector-container" ref={dropdownRef}>
      <button
        type="button"
        className="language-selector-button"
        onClick={() => setShowDropdown(!showDropdown)}
        aria-label={t('languageSelector.selectLanguage')}
        title={t('languageSelector.selectLanguage')}
      >
        <span className="language-indicator">
          {selectedTranscriptionLanguage.code.toUpperCase()}
        </span>
        <span className="language-icon">🌐</span>
        <span className="language-name">{selectedTranscriptionLanguage.nativeName}</span>
        <span className="language-arrow">{showDropdown ? '▲' : '▼'}</span>
      </button>

      {showDropdown && (
        <div className="language-dropdown-menu">
          <div className="language-header">
            <span className="language-header-title">UI + {t('home.buttons.record')}</span>
          </div>
          <div className="language-options">
            {SUPPORTED_LANGUAGES.map((language) => (
              <button
                key={language.code}
                type="button"
                onClick={() => handleLanguageSelect(language)}
                className={`language-option ${language.code === selectedTranscriptionLanguage.code ? 'selected' : ''}`}
              >
                <span className="language-option-code">{language.code.toUpperCase()}</span>
                <span className="language-option-name">{language.nativeName}</span>
                <span className="language-option-english">({language.name})</span>
                {language.code === selectedTranscriptionLanguage.code && (
                  <span className="check-mark">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
